import { useState } from 'react'
import { Bot, Copy, Check, X } from 'lucide-react'

export interface InlineSuggestedResponseProps {
  text: string
  onApply: (text: string) => void
  onDismiss?: () => void
  title?: string
}

export function InlineSuggestedResponse({
  text,
  onApply,
  onDismiss,
  title = 'Respuesta sugerida por IA',
}: InlineSuggestedResponseProps) {
  const [copied, setCopied] = useState(false)

  if (!text || !text.trim()) {
    return null
  }

  const handleCopy = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch {
      // Ignorar fallo de portapapeles en entornos sin permisos
    }
  }

  return (
    <div
      role="region"
      aria-label="Respuesta sugerida"
      className="mb-2.5 overflow-hidden rounded-xl border border-emerald-300/80 bg-gradient-to-r from-emerald-50/90 via-teal-50/50 to-white p-3 shadow-2xs backdrop-blur-xs transition-all"
    >
      <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-emerald-200/60">
        <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-900">
          <Bot className="h-3.5 w-3.5 text-emerald-600 animate-pulse" />
          <span>{title}</span>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Descartar sugerencia"
            className="rounded p-0.5 text-slate-400 hover:bg-emerald-100 hover:text-slate-700 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <p className="mt-2 text-xs leading-relaxed text-emerald-950 whitespace-pre-wrap break-words font-medium">
        {text}
      </p>

      <div className="mt-2.5 flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800 shadow-2xs hover:bg-emerald-50 active:scale-95 transition-all"
        >
          {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
          <span>{copied ? 'Copiado' : 'Copiar'}</span>
        </button>

        <button
          type="button"
          onClick={() => onApply(text)}
          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1 text-xs font-bold text-white shadow-2xs hover:bg-emerald-700 active:scale-95 transition-all"
        >
          <span>Usar en composer</span>
        </button>
      </div>
    </div>
  )
}
